import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { Grid } from "@mantine/core";
import Group from "@/components/atoms/Group";
import CTA from "@/components/atoms/CTA";
import Accordion from "@/components/atoms/accordions/DrawerAccordion";
import Text from "@/components/atoms/Text";
import Stack from "@/components/atoms/Stack";
import Spinner from "@/components/atoms/Spinner/Spinner";

import { GroupOrderClientSide, Order } from "@/types";
import { generateUUID } from "@/utils/generateUUID";
import Checkbox from "@/components/atoms/Checkbox";

interface OrdersTabProps {
  onClose: () => void;
  isLoading: boolean;
  orderIds: number[];
  taskId?: number;
  setGroupOrder: React.Dispatch<React.SetStateAction<GroupOrderClientSide[]>>;
  groupOrder?: GroupOrderClientSide[];
  orders: Order[];
  setOrders: React.Dispatch<React.SetStateAction<Order[]>>;
}

const OrdersTab: React.FC<OrdersTabProps> = (props) => {
  const { orders, setOrders, taskId = 0, setGroupOrder, groupOrder = [], isLoading } = props;
  const { user } = useAuth();
  const { t } = useTranslation();

  const [selectedOrders, setSelectedOrders] = useState<{
    [key: string]: boolean;
  }>({});

  const hasSelectedOrders = useMemo(() => {
    return Object.values(selectedOrders).some((isSelected) => isSelected);
  }, [selectedOrders]);

  const handleCheckboxChange = (orderId: string, checked: boolean) => {
    setSelectedOrders((prev) => ({
      ...prev,
      [orderId]: checked,
    }));
  };

  const createNewGroup = () => {
    if (!hasSelectedOrders) {
      alert(t("common:selectAtLeastOne"));
      return;
    }

    try {
      const selectedOrderEntries = Object.entries(selectedOrders);
      const ordersToGroup = orders.filter((order: Order) => {
        const orderEntry = selectedOrderEntries.find(([id, isSelected]) => id === order.id.toString() && isSelected);
        return !!orderEntry;
      });

      const groupEntries = ordersToGroup.map((order) => {
        const salesItem =
          user?.language.id && order.salesItem.nameTranslated?.[user.language.id]
            ? order.salesItem.nameTranslated[user.language.id]
            : order.salesItem.salesItem;
        const additionalInfo = `${order.properties[0] ? order.properties[0].value : ""}${order.properties[1] ? `, ${order.properties[1].value}` : ""} - ${order.amount}${order.unit.unit}`;
        return {
          groupName: `${salesItem.toUpperCase()} | ${additionalInfo}`,
          orderId: order.id,
        };
      });

      const newGroup: GroupOrderClientSide = {
        groupName: `${t("tasks:load")} ${generateUUID(5)}/${taskId}`,
        groups: groupEntries,
      };

      setGroupOrder([...groupOrder, newGroup]);

      const remainingOrders = orders.filter((order) => {
        const orderEntry = selectedOrderEntries.find(([id, isSelected]) => id === order.id.toString() && isSelected);
        return !orderEntry;
      });

      setOrders(remainingOrders);

      setSelectedOrders({});
    } catch (error) {
      console.error("Error creating transport group:", error);
    }
  };

  if (isLoading) {
    return <Spinner loading={true} inline={true} />;
  }

  if (orders.length === 0) {
    return (
      <Text size="sm" display="technical" tt="uppercase" strong={true}>
        {t("tasks:allOrdersHandled")}
      </Text>
    );
  }

  return (
    <Stack>
      <Accordion unstyled>
        {orders.map((order: Order) => {
          const productLabel =
            user?.language.id && order.salesItem.nameTranslated?.[user.language.id]
              ? order.salesItem.nameTranslated[user.language.id]
              : order.salesItem.salesItem;
          const additionalInfo = `${order.properties[0] ? order.properties[0].value : ""}${order.properties[1] ? `, ${order.properties[1].value}` : ""} - ${order.amount}${order.unit.unit}`;
          return (
            <Accordion.Item value={order?.id.toString()} key={order?.id}>
              <Grid align="center" justify="space-between">
                <Grid.Col span="auto" pl={10} mr={10}>
                  <Accordion.Control>
                    <Group>
                      <Text size="sm" display="technical" tt="uppercase" strong={true}>
                        {productLabel}
                      </Text>
                      {" | "}
                      <Text size="sm" display="technical" tt="uppercase" strong={true} color="secondary">
                        {additionalInfo}
                      </Text>
                    </Group>
                  </Accordion.Control>
                </Grid.Col>
                <Grid.Col span={1} pr={0}>
                  <Checkbox
                    data-testid={`orders-tab-checkbox-${order.id}`}
                    checked={selectedOrders[order?.id.toString()] || false}
                    onChange={(event) => handleCheckboxChange(order?.id.toString(), event.target.checked)}
                    onClick={(e: any) => e.stopPropagation()}
                  />
                </Grid.Col>
              </Grid>
              <Accordion.Panel>
                <OrderDetailsGrid order={order} t={t} />
              </Accordion.Panel>
            </Accordion.Item>
          );
        })}

        <CTA
          data-testid="orders-tab-new-load-btn"
          style={{ alignSelf: "flex-end", marginTop: 16 }}
          intent="primary"
          onClick={createNewGroup}
          disabled={!hasSelectedOrders}
        >
          {t("tasks:newLoad")}
        </CTA>
      </Accordion>
    </Stack>
  );
};

const OrderDetailsGrid = ({ order }: { order: Order; t: any }) => {
  const { user } = useAuth();

  const salesItemProperties = [...(order.salesItem.salesItemProperties ?? [])].sort(
    (a, b) => a.salesItemPropertyId - b.salesItemPropertyId
  );

  return (
    <Grid p="20px">
      {salesItemProperties.map((salesItemProperty) => {
        const matchingProperty = order.properties.find((p) => p.index === salesItemProperty.salesItemPropertyId);
        const label =
          salesItemProperty.salesItemPropertyTranslated.find((tr) => tr.languageId === user?.language.id)
            ?.propertyTranslated ?? salesItemProperty.salesItemPropertyName;

        return (
          <React.Fragment key={salesItemProperty.salesItemPropertyId}>
            <Grid.Col span={3}>
              <Text size="sm" color="secondary">
                {label}
              </Text>
            </Grid.Col>
            <Grid.Col span={9}>
              <Text size="sm">{matchingProperty?.value ?? "-"}</Text>
            </Grid.Col>
          </React.Fragment>
        );
      })}
    </Grid>
  );
};

export default OrdersTab;
